import React, { useState, useEffect, useRef } from 'react';
import { FiClock, FiAlertTriangle, FiSmile, FiX, FiCheck, FiMoon, FiLogOut } from 'react-icons/fi';
import { 
  getSastDateString, 
  getSastTimeString, 
  getAttendanceSettings, 
  recordClockIn, 
  recordClockOut,
  recordAfterHoursProgress,
  isAfterHoursNow,
  calculateAfterHoursIncrements,
  formatMinutes,
  remedyFridayAfterHours,
  remedyAllStaffAttendanceToday,
  DEFAULT_ATTENDANCE_SETTINGS 
} from '../attendanceService';

export default function AttendanceSessionMonitor({ user, staffMember, onForcedLogout }) {
  // Greeting notification state
  const [greeting, setGreeting] = useState(null); // { name, time, status, minutesLate, isAfterHours }

  // Standard shift warning bar state
  const [showShiftEndPrompt, setShowShiftEndPrompt] = useState(false);
  const [showTopWarningBar, setShowTopWarningBar] = useState(false);
  const [minutesUntilLogout, setMinutesUntilLogout] = useState(10);
  const [snoozeUntilMinutes, setSnoozeUntilMinutes] = useState(null);
  const [settings, setSettings] = useState(DEFAULT_ATTENDANCE_SETTINGS);

  // After-hours tracking state
  const [isAfterHours, setIsAfterHours] = useState(false);
  const [afterHoursMinutes, setAfterHoursMinutes] = useState(0);
  const [afterHoursIncrements, setAfterHoursIncrements] = useState(0);
  const [afterHoursClockedMinutes, setAfterHoursClockedMinutes] = useState(0);

  // Inactivity warning state during after hours
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [inactivityCountdown, setInactivityCountdown] = useState(120); // 2 minutes to respond

  const hasClockedInRef = useRef(false);
  const warningAcknowledgedRef = useRef(false);
  const lastActiveTimestampRef = useRef(Date.now());
  const afterHoursElapsedSecondsRef = useRef(0);
  const staffMemberRef = useRef(staffMember);
  staffMemberRef.current = staffMember;

  // Track user activity (mouse movement, keypress, click, scroll)
  useEffect(() => {
    const handleUserActivity = () => {
      lastActiveTimestampRef.current = Date.now();
      if (showInactivityWarning) {
        setShowInactivityWarning(false);
        setInactivityCountdown(120);
      }
    };

    window.addEventListener('mousemove', handleUserActivity, { passive: true });
    window.addEventListener('keydown', handleUserActivity, { passive: true });
    window.addEventListener('click', handleUserActivity, { passive: true });
    window.addEventListener('scroll', handleUserActivity, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('scroll', handleUserActivity);
    };
  }, [showInactivityWarning]);

  // Load attendance settings
  useEffect(() => {
    getAttendanceSettings().then((s) => {
      if (s) setSettings(s);
    });
  }, []);

  // 1. CLOCK IN & GREETING ON AUTH
  useEffect(() => {
    if (!user || !user.email) return;

    const runClockIn = async () => {
      const todayStr = getSastDateString();
      const lastSessionDate = localStorage.getItem('abacus_active_session_date');

      // Check for midnight rollover
      if (lastSessionDate && lastSessionDate !== todayStr) {
        console.log('[AttendanceSessionMonitor] Midnight rollover detected.');
        localStorage.removeItem('abacus_active_session_date');
        localStorage.removeItem('abacus_after_hours_seconds');
        if (onForcedLogout) {
          onForcedLogout('Midnight session expired. Please log in with Google to start today\'s shift.');
          return;
        }
      }

      localStorage.setItem('abacus_active_session_date', todayStr);

      const cleanEmail = (user?.email || '').toLowerCase().trim();
      const isTechnician = cleanEmail === 'technician@abacustelecomm.co.za';
      if (isTechnician) {
        remedyFridayAfterHours(cleanEmail);
      }
      // Run attendance accuracy audit on load
      remedyAllStaffAttendanceToday();

      const afterHoursCheck = isAfterHoursNow(staffMember, settings, new Date(), user?.email);
      setIsAfterHours(afterHoursCheck);

      if (!hasClockedInRef.current) {
        hasClockedInRef.current = true;
        const res = await recordClockIn(user, staffMember, settings);

        if (res && res.record) {
          const staffFirstName = staffMember?.first_name || user.displayName || user.email.split('@')[0].split('.')[0];
          const capName = staffFirstName.charAt(0).toUpperCase() + staffFirstName.slice(1);

          // Restore existing after-hours minutes if previously worked today
          const priorAfterHoursMinutes = Number(res.record.afterHoursMinutes) || 0;
          let initialAfterHoursMins = priorAfterHoursMinutes;

          if (initialAfterHoursMins > 0) {
            afterHoursElapsedSecondsRef.current = initialAfterHoursMins * 60;
            const incData = isTechnician
              ? calculateAfterHoursIncrements(initialAfterHoursMins)
              : { increments: initialAfterHoursMins, clockedMinutes: initialAfterHoursMins };
            setAfterHoursMinutes(initialAfterHoursMins);
            setAfterHoursIncrements(incData.increments);
            setAfterHoursClockedMinutes(incData.clockedMinutes);
            // Sync to firestore immediately
            recordAfterHoursProgress(staffMember?.id, user?.email, initialAfterHoursMins);
          } else if (afterHoursCheck) {
            // First time entering after-hours: start with 1 minute
            afterHoursElapsedSecondsRef.current = 60;
            const incData = isTechnician
              ? calculateAfterHoursIncrements(1)
              : { increments: 1, clockedMinutes: 1 };
            setAfterHoursMinutes(1);
            setAfterHoursIncrements(incData.increments);
            setAfterHoursClockedMinutes(incData.clockedMinutes);
          }

          // GREETING DISPLAY: Only pop up on initial clock-in of the day, never spam on mid-day refreshes
          const greetingKey = `abacus_greeting_shown_${todayStr}_${cleanEmail}`;
          const hasShownGreetingToday = localStorage.getItem(greetingKey);

          if (!hasShownGreetingToday || res.isNewClockIn) {
            localStorage.setItem(greetingKey, 'true');
            setGreeting({
              name: capName,
              time: res.record.clockInTime,
              status: res.record.status,
              minutesLate: res.record.minutesLate || 0,
              isAfterHours: afterHoursCheck
            });

            // Auto-hide greeting after 8 seconds
            setTimeout(() => {
              setGreeting(null);
            }, 8000);
          }
        }
      }
    };

    runClockIn();
  }, [user, staffMember, settings, onForcedLogout]);

  // 2. MAIN TICKER: RUNS EVERY 10 SECONDS
  // Manages: Midnight Cutoff, Shift-End Transition, and After-Hours 20-Minute Increments
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(async () => {
      const now = new Date();
      const currentDateStr = getSastDateString(now);
      const sessionDate = localStorage.getItem('abacus_active_session_date');

      // A) 12:00 AM MIDNIGHT FINAL CATCH-ALL
      if (sessionDate && sessionDate !== currentDateStr) {
        console.log('[AttendanceSessionMonitor] 12:00 AM Midnight reached. Enforcing final logout.');
        localStorage.removeItem('abacus_active_session_date');
        localStorage.removeItem('abacus_after_hours_seconds');
        clearInterval(interval);
        if (onForcedLogout) {
          onForcedLogout('12:00 AM Midnight reached. Daily session ended. Please log in tomorrow.');
        }
        return;
      }

      // Check current after-hours status
      const cleanEmail = (user?.email || '').toLowerCase().trim();
      const isTechnician = cleanEmail === 'technician@abacustelecomm.co.za';
      const currentAfterHours = isAfterHoursNow(staffMemberRef.current, settings, now, user?.email);

      // B) IF AFTER HOURS WORK IS ACTIVE -> TRACK 20-MINUTE INCREMENTS
      if (currentAfterHours) {
        setIsAfterHours(true);
        setShowTopWarningBar(false);
        setShowShiftEndPrompt(false);

        // Idle threshold: 20 minutes for technician (development side), 10 minutes for everyone else
        const idleThresholdSeconds = isTechnician ? (20 * 60) : (10 * 60);
        const idleSeconds = (Date.now() - lastActiveTimestampRef.current) / 1000;
        if (idleSeconds >= idleThresholdSeconds) {
          setShowInactivityWarning(true);
          setInactivityCountdown((prev) => {
            if (prev <= 10) {
              // Idle timeout reached: clock out and preserve completed work
              const finalMins = Math.floor(afterHoursElapsedSecondsRef.current / 60);
              recordClockOut(staffMemberRef.current?.id, user?.email, finalMins);
              if (onForcedLogout) {
                onForcedLogout(
                  isTechnician
                    ? 'After-hours session paused due to 20 minutes of inactivity. Completed 20-minute development blocks were safely saved.'
                    : 'After-hours session paused due to 10 minutes of inactivity. All work minutes were safely saved.'
                );
              }
              return 0;
            }
            return prev - 10;
          });
          return;
        }

        // Active after-hours work: increments by 10s of active interaction
        afterHoursElapsedSecondsRef.current += 10;
        const currentActiveMins = Math.max(1, Math.floor(afterHoursElapsedSecondsRef.current / 60));

        // For technician: 20-minute blocks (award each 20m block while interacting every 20m)
        // For the guys: per-minute precision
        const incData = isTechnician
          ? calculateAfterHoursIncrements(currentActiveMins)
          : { increments: currentActiveMins, clockedMinutes: currentActiveMins };

        setAfterHoursMinutes(currentActiveMins);
        setAfterHoursIncrements(incData.increments);
        setAfterHoursClockedMinutes(incData.clockedMinutes);

        // Sync to Firestore every 60 seconds (when seconds % 60 === 0)
        if (afterHoursElapsedSecondsRef.current % 60 === 0) {
          recordAfterHoursProgress(staffMemberRef.current?.id, user?.email, currentActiveMins);
        }

        return;
      }

      // C) REGULAR SHIFT HOURS (BEFORE 17:00 / END TIME)
      if (isAfterHours) {
        setIsAfterHours(false);
      }
      const currentTimeStr = getSastTimeString(now);
      const [currH, currM] = currentTimeStr.split(':').map(Number);
      const currentMinuteOfDay = currH * 60 + currM;

      const logoffTimeStr = staffMemberRef.current?.work_end_time || settings?.default_end_time || '17:00';
      const [endH, endM] = logoffTimeStr.split(':').map(Number);
      const shiftEndMinute = endH * 60 + endM;

      // Auto signout prompt target is 5 minutes after end time
      let targetLogoutMinute = shiftEndMinute + 5;
      if (snoozeUntilMinutes && snoozeUntilMinutes > targetLogoutMinute) {
        targetLogoutMinute = snoozeUntilMinutes;
      }

      const diff = targetLogoutMinute - currentMinuteOfDay;

      // Draw top warning bar when within 10 minutes of scheduled end time
      if (diff <= 10 && diff > 0 && !warningAcknowledgedRef.current) {
        setMinutesUntilLogout(diff);
        setShowTopWarningBar(true);
      }

      // Hit 5 minutes past shift end time: prompt user rather than hard logout
      if (diff <= 0 && !showShiftEndPrompt) {
        setShowTopWarningBar(false);
        setShowShiftEndPrompt(true);
      }

      // Hard auto-logout at shiftEnd + 8 minutes if shift-end prompt is ignored without snoozing
      if (diff <= -3 && showShiftEndPrompt) {
        handleManualClockOut();
        return;
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [user, settings, snoozeUntilMinutes, showShiftEndPrompt, onForcedLogout]);

  // Handle standard shift snooze
  const handleSnooze = () => {
    const now = new Date();
    const currentTimeStr = getSastTimeString(now);
    const [currH, currM] = currentTimeStr.split(':').map(Number);
    const snoozeDuration = Number(settings.snooze_minutes) || 15;
    const newTarget = currH * 60 + currM + snoozeDuration;

    setSnoozeUntilMinutes(newTarget);
    warningAcknowledgedRef.current = false;
    setShowTopWarningBar(false);
    setShowShiftEndPrompt(false);
  };

  // Transition to after hours work
  const handleContinueAfterHours = () => {
    setShowShiftEndPrompt(false);
    setShowTopWarningBar(false);
    setIsAfterHours(true);
    // Initialize 1 minute so the first 20m increment begins
    if (afterHoursElapsedSecondsRef.current === 0) {
      afterHoursElapsedSecondsRef.current = 60;
      const incData = calculateAfterHoursIncrements(1);
      setAfterHoursMinutes(1);
      setAfterHoursIncrements(incData.increments);
      setAfterHoursClockedMinutes(incData.clockedMinutes);
      recordAfterHoursProgress(staffMemberRef.current?.id, user?.email, 1);
    }
  };

  // Explicit Clock Out by user
  const handleManualClockOut = async () => {
    try {
      const finalMins = isAfterHours ? Math.floor(afterHoursElapsedSecondsRef.current / 60) : 0;
      await recordClockOut(staffMemberRef.current?.id, user?.email, finalMins);
    } catch (e) {
      console.warn('Clock-out error:', e);
    }
    if (onForcedLogout) {
      const msg = isAfterHours
        ? `Shift complete! You clocked ${afterHoursClockedMinutes} minutes (${afterHoursIncrements} x 20m blocks) of after-hours work.`
        : 'Shift complete! You have been logged out for the day.';
      onForcedLogout(msg);
    }
  };

  return (
    <>
      {/* 1. "HELLO =)" GREETING PILL (TOP RIGHT) */}
      {greeting && (
        <div className="fixed top-5 right-5 z-[9999] animate-in fade-in slide-in-from-top-4 duration-300">
          <div className="bg-white border-2 border-emerald-500/40 rounded-2xl shadow-xl p-4 flex items-center gap-3.5 max-w-sm">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${
              greeting.isAfterHours
                ? 'bg-indigo-50 text-indigo-600 border-indigo-200'
                : 'bg-emerald-50 text-emerald-600 border-emerald-200'
            }`}>
              {greeting.isAfterHours ? <FiMoon className="w-5 h-5" /> : <FiSmile className="w-6 h-6" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-extrabold text-[#161a2e] text-sm">
                  Hello {greeting.name} =)
                </span>
                {greeting.isAfterHours ? (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                    After Hours
                  </span>
                ) : greeting.status === 'late' ? (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                    +{greeting.minutesLate}m late
                  </span>
                ) : (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    On Time
                  </span>
                )}
              </div>
              <p className="text-xs text-[#575e74] mt-0.5 flex items-center gap-1 font-mono">
                <FiClock className="w-3 h-3 text-[#7b8195]" />
                {greeting.isAfterHours ? `Session started at ${greeting.time}` : `Clocked in at ${greeting.time}`}
              </p>
            </div>
            <button
              onClick={() => setGreeting(null)}
              className="text-[#7b8195] hover:text-[#161a2e] p-1 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
              title="Dismiss"
            >
              <FiX className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* 2. AFTER-HOURS WORK ACTIVE: RED CLOCK OUT & LEAVE BUTTON */}
      {isAfterHours && (
        <div className="fixed top-2.5 right-3 md:right-5 z-[10001] animate-in fade-in duration-300">
          <button
            type="button"
            onClick={handleManualClockOut}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-600 hover:bg-rose-500 active:bg-rose-700 text-white rounded-lg text-xs font-bold transition shadow-sm hover:shadow cursor-pointer border border-rose-500/30"
            title="Clock out and leave after hours"
          >
            <FiLogOut className="w-3.5 h-3.5" />
            <span>Clock Out & Leave</span>
          </button>
        </div>
      )}

      {/* 3. SHIFT-END TRANSITION MODAL (AT 17:05 ON REGULAR WORKDAYS) */}
      {showShiftEndPrompt && !isAfterHours && (
        <div className="fixed inset-0 z-[10001] bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white border border-[#e6e8ee] rounded-2xl shadow-2xl max-w-md w-full p-6 text-center space-y-4">
            <div className="w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200 text-amber-600 flex items-center justify-center mx-auto">
              <FiClock className="w-7 h-7" />
            </div>

            <div className="space-y-1">
              <h3 className="text-lg font-black text-[#161a2e]">
                Scheduled Shift Complete
              </h3>
              <p className="text-xs text-[#575e74] leading-relaxed">
                Your standard working hours have concluded. Would you like to clock out for the day, or continue working After Hours?
              </p>
            </div>

            <div className="p-3 bg-indigo-50/60 border border-indigo-200 rounded-xl text-left text-xs text-indigo-900 space-y-1">
              <div className="font-bold flex items-center gap-1.5">
                <FiMoon className="w-4 h-4 text-indigo-600" />
                <span>After Hours Rate Rule</span>
              </div>
              <p className="text-[11px] text-indigo-700 leading-normal">
                After-hours work is automatically clocked in <strong>20-minute increments</strong> (e.g. 1-20m = 20m, 21-40m = 40m).
              </p>
            </div>

            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={handleManualClockOut}
                className="flex-1 py-2.5 px-4 rounded-xl text-xs font-bold text-[#575e74] bg-[#f1f2f6] hover:bg-[#e6e8ee] transition cursor-pointer"
              >
                Clock Out for the Day
              </button>
              <button
                type="button"
                onClick={handleContinueAfterHours}
                className="flex-1 py-2.5 px-4 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-500 shadow-xs transition cursor-pointer"
              >
                Work After Hours
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 4. AFTER-HOURS INACTIVITY WARNING BAR */}
      {showInactivityWarning && (
        <div className="fixed top-12 left-0 right-0 z-[10002] bg-rose-950 text-white border-b-2 border-rose-500 shadow-2xl px-4 py-2.5 flex items-center justify-between gap-3 text-xs animate-in slide-in-from-top duration-200">
          <div className="flex items-center gap-2">
            <FiAlertTriangle className="w-4 h-4 text-rose-400 animate-bounce" />
            <span>
              <strong>After Hours Idle Check:</strong> No user activity detected for {isAfterHours && user?.email?.toLowerCase().trim() === 'technician@abacustelecomm.co.za' ? '20' : '10'} minutes. Auto-saving and clocking out in <strong className="text-rose-300 font-mono font-black">{inactivityCountdown}s</strong>.
            </span>
          </div>
          <button
            type="button"
            onClick={() => {
              lastActiveTimestampRef.current = Date.now();
              setShowInactivityWarning(false);
              setInactivityCountdown(120);
            }}
            className="px-3 py-1 bg-white text-rose-950 hover:bg-rose-100 rounded-lg text-xs font-black transition cursor-pointer"
          >
            I'm Still Working
          </button>
        </div>
      )}

      {/* 5. REGULAR SHIFT 10-MINUTE WARNING BAR */}
      {showTopWarningBar && !isAfterHours && (
        <div className="fixed top-0 left-0 right-0 z-[10000] bg-[#161a2e] text-white border-b-2 border-amber-500 shadow-lg px-4 py-2 flex flex-wrap items-center justify-between gap-3 text-xs animate-in slide-in-from-top duration-300">
          <div className="flex items-center gap-2 font-medium">
            <span className="p-1 rounded-md bg-amber-500/20 text-amber-400">
              <FiClock className="w-4 h-4 animate-pulse" />
            </span>
            <span>
              Scheduled shift ends in <strong className="text-amber-400 font-bold">{minutesUntilLogout} minute{minutesUntilLogout !== 1 ? 's' : ''}</strong>.
            </span>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => {
                warningAcknowledgedRef.current = true;
                setShowTopWarningBar(false);
              }}
              className="px-3 py-1 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-bold transition cursor-pointer border border-white/20"
            >
              OK
            </button>
            <button
              type="button"
              onClick={handleSnooze}
              className="px-3 py-1 bg-amber-500 hover:bg-amber-400 text-[#161a2e] rounded-lg text-xs font-black transition shadow-sm cursor-pointer"
            >
              Snooze (+{settings.snooze_minutes || 15}m)
            </button>
            <button
              type="button"
              onClick={handleContinueAfterHours}
              className="px-3 py-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold transition cursor-pointer"
            >
              Switch to After Hours
            </button>
          </div>
        </div>
      )}
    </>
  );
}
